import React, { useState } from 'react';
import axios, { AxiosError } from 'axios';
import toast from '../../../utils/toast';
import { apiRoute } from '../../../constants/api-constants';
import { useAuth } from '../../../context/auth-provider';
import { RightArrow } from '../../icons/right-arrow';
import {
  Field,
  FieldError,
  Label,
  PasswordWrap,
  PasswordInput,
  PasswordToggle,
  FullWidthButton,
  HelperText,
} from '../../onboarding/onboarding.styled';
import { Shake, StatusLabel } from '../../../motion';
import { SectionHeading, SectionLead } from './account.styled';

/**
 * CHANGE YOUR OWN PASSWORD — every role, including residents.
 *
 * Lifted verbatim out of account-page.tsx, which used to BE this form. The page
 * is now a shell with four sections and this is one of them; nothing about the
 * request, the validation or the copy changed in the move.
 *
 * The endpoint (POST /password/change) is mounted before the staff-only gate in
 * the backend for the same reason this section is reachable without any role or
 * permission entry: rotating your own password is not a staff capability.
 */
export const AccountPassword: React.FC = () => {
  const { user } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  /* Counts REFUSALS, so the shake replays on a second one with the same
     message — the CSS keyframe it replaces only ran when hasError flipped. */
  const [attempt, setAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const refuse = (message: string) => {
    setError(message);
    setAttempt(count => count + 1);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!current) {
      refuse('Enter your current password');

      return;
    }
    if (next.length < 8) {
      refuse('Password must be at least 8 characters');

      return;
    }
    if (next !== confirm) {
      refuse('Passwords do not match');

      return;
    }
    setError('');
    setSubmitting(true);
    try {
      // The default axios instance, not publicApi: this call is authenticated
      // and wants the session cookies plus the CSRF echo the app instance adds.
      const res = await axios.post(`${apiRoute}/password/change`, {
        current_password: current,
        new_password: next,
      });
      // The server revoked every other device and re-issued THIS browser's
      // cookies, so nothing local needs re-fetching — the session is still the
      // same person.
      toast.success(res.data?.message || 'Your password has been changed.');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      const ax = err as AxiosError<{ message?: string }>;
      refuse(ax.response?.data?.message || 'Could not change your password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SectionHeading>Change your password</SectionHeading>
      <SectionLead>
        Choose a new password for your Lobbero account. Every other device is
        signed out; this one stays signed in.
        {user?.email ? ` Signed in as ${user.email}.` : ''}
      </SectionLead>

      <form onSubmit={submit}>
        {/* The three fields refuse together — one error line covers them —
            so they shake together, replayed per attempt. */}
        <Field>
          <Label htmlFor="ac-current">Current password</Label>
          <Shake active={!!error} attempt={attempt}>
            <PasswordWrap>
              <PasswordInput
                id="ac-current"
                name="current_password"
                type={show ? 'text' : 'password'}
                value={current}
                onChange={e => setCurrent(e.target.value)}
                hasError={!!error}
                autoComplete="current-password"
              />
              <PasswordToggle type="button" onClick={() => setShow(s => !s)}>
                {show ? 'Hide' : 'Show'}
              </PasswordToggle>
            </PasswordWrap>
          </Shake>
        </Field>
        <Field>
          <Label htmlFor="ac-new">New password</Label>
          <Shake active={!!error} attempt={attempt}>
            <PasswordInput
              id="ac-new"
              name="new_password"
              type={show ? 'text' : 'password'}
              value={next}
              onChange={e => setNext(e.target.value)}
              hasError={!!error}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </Shake>
        </Field>
        <Field>
          <Label htmlFor="ac-confirm">Confirm new password</Label>
          <Shake active={!!error} attempt={attempt}>
            <PasswordInput
              id="ac-confirm"
              name="confirm_password"
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              hasError={!!error}
              autoComplete="new-password"
            />
          </Shake>
        </Field>
        <FieldError message={error || undefined} />
        {/* The label swaps without the button changing width. */}
        <FullWidthButton
          type="submit"
          disabled={submitting}
          aria-busy={submitting}
        >
          <StatusLabel
            state={submitting ? 'busy' : 'idle'}
            labels={{
              idle: (
                <>
                  Change password <RightArrow />
                </>
              ),
              busy: 'Saving…',
            }}
          />
        </FullWidthButton>
      </form>

      <HelperText>
        Forgotten it instead? Sign out and use “Forgot your password?” on the
        sign-in screen — that link is emailed to you and does not need the old
        password.
      </HelperText>
    </>
  );
};
